import { AppBreadcrumbs } from "@/components/layouts/app-breadcrumbs";
import { AppEntityTable } from "@/components/blocks/app-entity-table";
import { PageBackButton } from "@/components/elements/page-back-button";
import { SearchInput } from "@/components/elements/search-input";
import { HeaderMenu } from "@/components/layouts/header-menu";
import PageLayout from "@/components/layouts/page.layout";
import { PAGE_META } from "@/config/page-meta";
import { RoutePaths } from "@/config/route-paths";
import { useAuth } from "@/contexts/auth/use-auth";
import { CredentialExpiryDate } from "@/features/credentials/pages/list/components/credential-expiry-date";
import {
  CredentialNameCell,
  expiredCredentialRowClassName,
} from "@/features/credentials/pages/list/components/credential-name-cell";
import { EmptyCredentialsList } from "@/features/credentials/pages/list/components/empty-credentials-list";
import { ErrorCredentialsList } from "@/features/credentials/pages/list/components/error-credentials-list";
import { FilteredEmptyCredentialsList } from "@/features/credentials/pages/list/components/filtered-empty-credentials-list";
import { LoadingCredentialsList } from "@/features/credentials/pages/list/components/loading-credentials-list";
import { CredentialRowActions } from "@/features/credentials/pages/list/components/row-actions";
import { useCredentialsListParams } from "@/features/credentials/pages/list/hooks/use-credentials-list-params";
import { formatCredentialType } from "@/features/credentials/pages/list/lib/format-credential-type";
import { getCredentialsListBreadcrumbs } from "@/features/credentials/pages/list/lib/get-credentials-list-breadcrumbs";
import { useCredentialsListQuery } from "@/features/credentials/queries/use-credentials-list";
import { useOrganizationFromListCache } from "@/features/organizations/queries/use-organization-from-list-cache";
import { useAppListSearch } from "@/hooks/use-app-list-search";
import { usePageMeta } from "@/hooks/use-page-meta";
import { Button, ListSection, ListSectionFilters, PageHeader, SectionWithHeading } from "@cmde/shadcn";
import { PlusIcon } from "lucide-react";
import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { Link, Outlet } from "react-router";

export default function CredentialListPage() {
  const { t } = useTranslation();
  const { customerName } = useAuth();
  const { customerId, organizationId, sport } = useCredentialsListParams();
  const organization = useOrganizationFromListCache(customerId, organizationId);
  const credentialsListQuery = useCredentialsListQuery({ sport, organizationId });

  const { inputValue, debouncedQuery, setInputValue, filteredData } = useAppListSearch({
    data: credentialsListQuery.data ?? [],
    keys: ["name", "username", "credentialType"],
    debounceDelay: 300,
  });

  const createHref = RoutePaths.credentials.create({ customerId, organizationId, sport });

  const breadcrumbs = useMemo(
    () =>
      getCredentialsListBreadcrumbs({
        sport,
        customerId,
        customerName,
        organizationId,
        organizationName: organization?.name ?? `Organization ${organizationId}`,
      }),
    [sport, customerId, customerName, organizationId, organization],
  );

  const showFilteredEmpty =
    !credentialsListQuery.isLoading &&
    !credentialsListQuery.isError &&
    (credentialsListQuery.data?.length ?? 0) > 0 &&
    debouncedQuery.length > 0 &&
    filteredData.length === 0;

  return (
    <>
      {usePageMeta(PAGE_META.credentials)}
      <PageLayout>
        <div className="h-full">
          <PageHeader
            title={t("credentials.list.messages.title")}
            subtitle={t("credentials.list.messages.subtitle")}
            backAction={<PageBackButton href={RoutePaths.organizations.list({ customerId })} />}
            breadcrumbs={<AppBreadcrumbs breadcrumbs={breadcrumbs} />}
            secondaryNavigation={<HeaderMenu />}
          />

          <SectionWithHeading
            heading=""
            action={
              <Button asChild>
                <Link to={createHref}>
                  <PlusIcon />
                  {t("credentials.create.action")}
                </Link>
              </Button>
            }
          >
            <ListSection
              header={
                <ListSectionFilters
                  searchComponent={
                    <SearchInput
                      value={inputValue}
                      onChange={setInputValue}
                      placeholder={t("credentials.list.search.placeholder")}
                      disabled={credentialsListQuery.isLoading}
                    />
                  }
                />
              }
            >
              {showFilteredEmpty ? (
                <FilteredEmptyCredentialsList />
              ) : (
                <AppEntityTable
                  queryResult={credentialsListQuery}
                  data={filteredData}
                  tbodyClassName={expiredCredentialRowClassName}
                  columns={[
                    {
                      header: t("credentials.list.table.name"),
                      key: "name",
                      cell: (credential) => (
                        <CredentialNameCell name={credential.name} expiryDate={credential.expiryDate} />
                      ),
                    },
                    {
                      header: t("credentials.list.table.username"),
                      key: "username",
                      cell: (credential) => credential.username,
                    },
                    {
                      header: t("credentials.list.table.user_type"),
                      key: "credentialType",
                      cell: (credential) => formatCredentialType(credential.credentialType, t),
                    },
                    {
                      header: t("credentials.list.table.expiry_date"),
                      key: "expiryDate",
                      cell: (credential) => <CredentialExpiryDate expiryDate={credential.expiryDate} />,
                    },
                    {
                      header: t("credentials.list.table.actions"),
                      key: "actions",
                      width: "140px",
                      cell: (credential) => (
                        <CredentialRowActions
                          credential={credential}
                          customerId={customerId}
                          organizationId={organizationId}
                          sport={sport}
                        />
                      ),
                    },
                  ]}
                  getKey={(credential) => credential.credentialId}
                  loadingContent={<LoadingCredentialsList />}
                  errorContent={
                    <ErrorCredentialsList
                      retry={() => credentialsListQuery.refetch()}
                      errorMessage={credentialsListQuery.error?.message}
                    />
                  }
                  emptyContent={<EmptyCredentialsList createHref={createHref} />}
                />
              )}
            </ListSection>
          </SectionWithHeading>
        </div>
      </PageLayout>
      <Outlet />
    </>
  );
}
